import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Bell, ExternalLink, Megaphone, Settings, X } from 'lucide-react'
import { useFeatureFlags } from '../../contexts/FeatureFlagContext'
import { countUnreadOutsideActiveContext } from '../../hooks/notifications/countUnreadOutsideActiveContext'
import { shouldShowNotificationAttention } from '../../hooks/notifications/notificationAttention'
import { useAutoReadVisibleNotifications } from '../../hooks/notifications/useAutoReadVisibleNotifications'
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  usePersonalNotifications,
  useUnreadNotificationCount,
} from '../../hooks/notifications/useNotifications'
import { personalNotificationCopy } from '../../hooks/notifications/personalNotificationCopy'
import { type PersonalNotification } from '../../hooks/notifications/types'
import { useFeedback } from '../feedback/FeedbackContext'

function formatWhen(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

function NotificationItemRow({
  item,
  onOpen,
}: {
  item: PersonalNotification
  onOpen: (item: PersonalNotification) => void
}) {
  const { t, i18n } = useTranslation('dashboard')
  const copy = personalNotificationCopy(item)
  const task =
    typeof copy.params?.taskKey === 'string'
      ? t(copy.params.taskKey, {
          defaultValue: item.payload.flow_name || 'Flow',
        })
      : undefined
  const title = t(copy.titleKey)
  const body = t(copy.bodyKey, task != null ? { task } : undefined)

  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className={[
        'w-full text-left px-4 py-3 border-b border-[#2a2a2a] hover:bg-[#2a2a2a] transition-colors',
        item.read_at ? 'opacity-70' : '',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {!item.read_at && <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A00] shrink-0" />}
            <p className="text-sm font-medium text-white truncate">{title}</p>
          </div>
          <p className="mt-0.5 text-xs text-gray-400 line-clamp-2">{body}</p>
          {item.payload.flow_name && (
            <p className="mt-1 text-xs text-gray-500 truncate">{String(item.payload.flow_name)}</p>
          )}
        </div>
        <span className="text-[10px] text-gray-500 whitespace-nowrap shrink-0">
          {formatWhen(item.created_at, i18n.language)}
        </span>
      </div>
    </button>
  )
}

export function NotificationBell() {
  const { t } = useTranslation('dashboard')
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const { isNotificationsEnabled, isQuackbackConfigured } = useFeatureFlags()
  const enabled = isNotificationsEnabled()
  const quackbackOn = isQuackbackConfigured()

  const [open, setOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const { data: unread } = useUnreadNotificationCount(enabled)
  // Always fetch while the feature is on so auto-read can suppress the badge
  // for the flow the user is already watching (shared cache with the panel).
  const { data: items = [], isLoading, refetch } = usePersonalNotifications(enabled)
  useAutoReadVisibleNotifications(enabled)
  const markRead = useMarkNotificationRead()
  const markAll = useMarkAllNotificationsRead()
  const {
    ready: quackReady,
    newsUnread,
    openChangelog,
    openPortal,
  } = useFeedback()

  const personalUnread = countUnreadOutsideActiveContext(
    unread?.personal ?? 0,
    items,
    pathname,
    search,
  )
  const showDot = shouldShowNotificationAttention(personalUnread, newsUnread)

  useEffect(() => {
    if (!open) return
    void refetch()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        panelRef.current &&
        !panelRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClick)
    }
  }, [open, refetch])

  if (!enabled) return null

  const handleOpenItem = async (item: PersonalNotification) => {
    if (!item.read_at) {
      try {
        await markRead.mutateAsync(item.id)
      } catch {
        /* non-fatal */
      }
    }
    setOpen(false)
    const link = item.payload.deep_link
    if (typeof link === 'string' && link.startsWith('/')) {
      navigate(link)
    }
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('notifications.center.ariaLabel')}
        aria-expanded={open}
        className="relative flex items-center justify-center w-9 h-9 rounded-full text-gray-300 hover:text-white hover:bg-[#1f1f1f] transition"
      >
        <Bell className="w-5 h-5" />
        {showDot && (
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[#FF5A00] ring-2 ring-[#0d0d0d]" />
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label={t('notifications.center.title')}
          className="absolute right-0 mt-2 w-[min(100vw-1.5rem,360px)] max-h-[min(80vh,560px)] flex flex-col bg-[#212121] border border-[#333] rounded-lg shadow-2xl z-50 overflow-hidden"
        >
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[#333] shrink-0">
            <h2 className="text-sm font-semibold text-white font-switzer truncate">
              {t('notifications.center.title')}
            </h2>
            <div className="flex items-center gap-1">
              {personalUnread > 0 && (
                <button
                  type="button"
                  onClick={() => markAll.mutate()}
                  className="text-xs text-gray-400 hover:text-white px-2 py-1 rounded"
                >
                  {t('notifications.center.markAllRead')}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setOpen(false)
                  navigate('/profile#notification-preferences')
                }}
                aria-label={t('notifications.center.openSettings')}
                className="p-1 rounded text-gray-400 hover:text-white hover:bg-[#333]"
              >
                <Settings className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t('notifications.center.close')}
                className="p-1 rounded text-gray-400 hover:text-white hover:bg-[#333]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto min-h-0">
            {isLoading && (
              <p className="px-4 py-6 text-sm text-gray-500 text-center">…</p>
            )}
            {!isLoading && items.length === 0 && (
              <div className="px-4 py-8 text-center">
                <p className="text-sm font-medium text-white">{t('notifications.center.emptyTitle')}</p>
                <p className="mt-1 text-xs text-gray-400">{t('notifications.center.emptyBody')}</p>
              </div>
            )}
            {!isLoading &&
              items.map((item) => (
                <NotificationItemRow key={item.id} item={item} onOpen={handleOpenItem} />
              ))}
          </div>

          {quackbackOn && (
            <div className="shrink-0 border-t border-[#333] px-4 py-3 space-y-2">
              <button
                type="button"
                disabled={!quackReady}
                onClick={() => {
                  openChangelog()
                  setOpen(false)
                }}
                className="w-full flex items-center justify-center gap-2 rounded-md bg-[#2a2a2a] hover:bg-[#333] disabled:opacity-50 px-3 py-2 text-sm text-white transition relative"
              >
                <Megaphone className="w-4 h-4 text-emerald-400" />
                <span>{t('notifications.center.openNews')}</span>
                {newsUnread > 0 && (
                  <span
                    className="absolute right-3 top-1/2 -translate-y-1/2 min-w-[1.25rem] h-5 px-1.5 rounded-full bg-[#FF5A00] text-[10px] font-semibold text-white flex items-center justify-center"
                    aria-label={t('notifications.center.newsUnread', { count: newsUnread })}
                  >
                    {newsUnread > 9 ? '9+' : newsUnread}
                  </span>
                )}
              </button>
              <button
                type="button"
                disabled={!quackReady}
                onClick={() => {
                  openPortal()
                  setOpen(false)
                }}
                className="w-full flex items-center justify-center gap-2 rounded-md bg-[#2a2a2a] hover:bg-[#333] disabled:opacity-50 px-3 py-2 text-sm text-white transition"
              >
                <ExternalLink className="w-4 h-4 text-[#FF5A00]" />
                {t('notifications.center.openPortal')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
